import { comparePdfLabels, pdfHeaderText } from "./pdfTagName";
import type { State } from "../State";
import type Consumer from "../models/Consumer";
import { roombookButtonProductName } from "./roombookButtonProductName";
import { ChannelType, channelTypeInfo } from "../models/ChannelType";
import type { C1_RaumbuchPdfCategory, C1_RaumbuchPdfRoom } from "../../ConceptOne/src/raumbuch/C1_RaumbuchPdf";

/** Assigns each classic consumer to exactly one functional section. */
function consumerCategory(consumer: Consumer): string {
  const info = channelTypeInfo(consumer.channelType);
  if (info.isSupply || consumer.image?.includes("Plug")) return "Steckdosen / Stromversorgung";
  if (info.isLight) return "Beleuchtung";
  if (info.isRollo) return "Beschattung";
  if (info.isHeating) return "Heizung";
  if (consumer.channelType === ChannelType.Lock) return "Türschlösser";
  return "Weitere Funktionen";
}

/** Reads the classic model directly; no C1 facets, equipment groups or derived C1 totals are needed. */
export function collectClassicRoombook(app: State): C1_RaumbuchPdfRoom[] {
  const result: C1_RaumbuchPdfRoom[] = [];
  const floors = [...app.floors.asArray()].sort((a, b) => comparePdfLabels(a, b));
  for (const floor of floors) {
    const rooms = [...floor.rooms.asArray()].sort((a, b) => comparePdfLabels(a, b));
    for (const room of rooms) {
      if (room.isDeleted) continue;
      const categories = new Map<string, C1_RaumbuchPdfCategory>();
      const add = (title: string, item: C1_RaumbuchPdfCategory["items"][number]) => {
        let category = categories.get(title);
        if (!category) { category = { title, items: [] }; categories.set(title, category); }
        category.items.push(item);
      };
      for (const button of room.buttons.asArray()) {
        if (button.isDeleted) continue;
        add(button.isDecentralActor ? "Dezentrale Aktoren" : "Taster / Sensoren", {
          tag: button.tag, name: button.name, details: roombookButtonProductName(button.template?.name),
        });
      }
      for (const consumer of room.consumers.asArray()) {
        if (consumer.isDeleted) continue;
        add(consumerCategory(consumer), { tag: consumer.tag, name: consumer.name,
          consumerImage: consumer.image,
          details: [app.translate(`channelType.${consumer.channelType}`, consumer.channelType), consumer.quantity !== 1 ? `${consumer.quantity} Stück` : ""].filter(Boolean).join(" · ") });
      }
      const notes = (room.page?.memos.asArray() ?? [])
        .filter(memo => !memo.isDeleted && !memo.isRoomTag && memo.name.trim())
        .map(memo => memo.name);
      if (room.isFloorGeneralRoom && !categories.size && !notes.length) continue;
      const ordered: C1_RaumbuchPdfCategory[] = [];
      for (const title of ["Taster / Sensoren", "Dezentrale Aktoren", "Beleuchtung", "Beschattung", "Heizung", "Türschlösser", "Steckdosen / Stromversorgung", "Weitere Funktionen"]) {
        const category = categories.get(title);
        if (!category) continue;
        category.items.sort((a, b) => comparePdfLabels(a, b));
        ordered.push(category);
      }
      result.push({ id: room.id, floorLabel: pdfHeaderText(floor),
        fullTag: room.fullTag, floorName: floor.name, roomName: room.name,
        floorTag: floor.tag, roomTag: room.tag,
        roomLabel: room.isFloorGeneralRoom ? "Allgemeiner Bereich" : pdfHeaderText(room), categories: ordered, notes });
    }
  }
  return result;
}
